/*
 * ROYDED offer analytics transport.
 *
 * Emits consent-aware view/interaction/add events for storefront offer
 * surfaces (cart drawer upsell, bundle strip, bundle PDP) to the ROYDED Ops
 * Worker. Disabled entirely unless an endpoint is configured and analytics
 * consent has been explicitly granted. Never blocks or alters existing
 * storefront behaviour — every failure mode here is silent (at most a single
 * console.debug line).
 */
(function () {
  if (window.RDOfferEvents) return;

  var ENDPOINT = ((document.documentElement.dataset && document.documentElement.dataset.rdOfferEndpoint) || '').trim();
  if (!ENDPOINT) return;

  var SCHEMA_VERSION = 1;
  var MAX_BATCH = 20;
  var FLUSH_DELAY_MS = 2000;
  var VIEW_VISIBLE_RATIO = 0.5;
  var VIEW_DWELL_MS = 1000;
  var VALID_INTERACTIONS = { primary_cta: true, size_selection: true, add_cta: true };

  var consentAllowed = false;
  var consentObservedAt = null;
  var queue = [];
  var flushTimer = null;
  var surfaceMutationObserver = null;
  var surfaceState = typeof WeakMap === 'function' ? new WeakMap() : null;
  var observedDrawers = typeof WeakSet === 'function' ? new WeakSet() : null;

  function debug(message, error) {
    if (window.console && console.debug) {
      console.debug('rd-offer-events: ' + message, error || '');
    }
  }

  function uuid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (error) {
      // fall through to manual fallback
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function nowIso() {
    return new Date().toISOString();
  }

  // ---- Traffic class ---------------------------------------------------
  // Lets Ops leave out testing. Visiting any page with ?rd_internal=1 marks
  // this browser as ROYDED's own for a year; ?rd_internal=0 clears it.

  function rememberInternal() {
    try {
      var flag = new URLSearchParams(window.location.search).get('rd_internal');
      if (flag === '1') document.cookie = 'rd_internal=1; Max-Age=31536000; Path=/; SameSite=Lax; Secure';
      if (flag === '0') document.cookie = 'rd_internal=; Max-Age=0; Path=/; SameSite=Lax; Secure';
    } catch (error) {
      // ignore
    }
  }
  rememberInternal();

  function trafficClass() {
    try {
      if (/(?:^|;\s*)rd_internal=1(?:;|$)/.test(document.cookie)) return 'internal';
      if (navigator.webdriver === true) return 'automation';
      var shop = window.Shopify;
      if (shop && (shop.designMode || (shop.theme && shop.theme.role && shop.theme.role !== 'main'))) return 'preview';
    } catch (error) {
      // fall through
    }
    return 'live';
  }

  // Anonymous per-tab session, so Ops can count people rather than page loads.
  function sessionId() {
    try {
      var id = window.sessionStorage.getItem('rd_offer_session');
      if (!id) {
        id = uuid();
        window.sessionStorage.setItem('rd_offer_session', id);
      }
      return id;
    } catch (error) {
      return undefined;
    }
  }

  function activeCurrency() {
    try {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    } catch (error) {
      return '';
    }
  }

  function gpcBlocked() {
    try {
      return navigator.globalPrivacyControl === true;
    } catch (error) {
      return false;
    }
  }

  // ---- Consent -------------------------------------------------------

  function setConsent(allowed) {
    var wasAllowed = consentAllowed;
    consentAllowed = !!allowed;
    consentObservedAt = nowIso();
    if (consentAllowed && !wasAllowed) startObserving();
    if (!consentAllowed) {
      queue = [];
      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
    }
  }

  function evaluateConsent() {
    if (gpcBlocked()) {
      setConsent(false);
      return;
    }
    try {
      if (
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
      ) {
        setConsent(window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true);
      }
    } catch (error) {
      debug('consent check failed', error);
    }
  }

  function loadConsentApi() {
    if (gpcBlocked()) {
      setConsent(false);
      return;
    }
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (errors) {
        if (errors) {
          debug('consent-tracking-api unavailable', errors);
          return;
        }
        evaluateConsent();
      });
    } catch (error) {
      debug('loadFeatures threw', error);
    }
  }

  document.addEventListener('visitorConsentCollected', function () {
    evaluateConsent();
  });

  // ---- Batching / transport ------------------------------------------

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(function () {
      flushTimer = null;
      flush();
    }, FLUSH_DELAY_MS);
  }

  function enqueue(event) {
    if (!consentAllowed || gpcBlocked()) return;
    queue.push(event);
    if (queue.length >= MAX_BATCH) {
      flush();
    } else {
      scheduleFlush();
    }
  }

  function flush() {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (!queue.length || !consentAllowed || gpcBlocked()) {
      queue = [];
      return;
    }
    var events = queue.splice(0, MAX_BATCH);
    send(events);
    if (queue.length) scheduleFlush();
  }

  function send(events) {
    try {
      var request = fetch(ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: events })
      });
      if (request && typeof request.then === 'function') {
        request
          .then(function (response) {
            if (!response || response.status !== 202) {
              debug('unexpected response status', response && response.status);
            }
            // Drain the small acknowledgement so keepalive requests finish
            // cleanly instead of retaining an unread response stream.
            if (response && typeof response.text === 'function') return response.text();
          })
          .catch(function (error) {
            debug('send failed', error);
          });
      }
    } catch (error) {
      debug('send threw', error);
    }
  }

  window.addEventListener('pagehide', flush);

  // ---- Event construction ---------------------------------------------

  function baseEvent(eventName) {
    return {
      schemaVersion: SCHEMA_VERSION,
      eventId: uuid(),
      eventName: eventName,
      occurredAt: nowIso(),
      offerVersion: 'v1',
      currency: activeCurrency(),
      consent: {
        analyticsAllowed: true,
        source: 'shopify_customer_privacy',
        observedAt: consentObservedAt || nowIso()
      },
      source: 'first_party_transport',
      trafficClass: trafficClass(),
      sessionId: sessionId()
    };
  }

  function surfaceInfo(el) {
    return {
      offerId: el.getAttribute('data-rd-offer') || '',
      placement: el.getAttribute('data-rd-offer-placement') || '',
      offerType: el.getAttribute('data-rd-offer-type') || '',
      productId: el.getAttribute('data-rd-offer-product') || '',
      variantId: el.getAttribute('data-rd-offer-variant') || ''
    };
  }

  function isValidSurface(info) {
    return !!(info.offerId && info.placement && info.offerType);
  }

  // ---- View tracking (IntersectionObserver) ----------------------------

  function getState(el) {
    if (!surfaceState) return null;
    var state = surfaceState.get(el);
    if (!state) {
      state = { surfaceInstanceId: uuid(), viewed: false, timer: null, observer: null };
      surfaceState.set(el, state);
    }
    return state;
  }

  function emitView(el, state) {
    var info = surfaceInfo(el);
    if (!isValidSurface(info)) return;
    var event = baseEvent('royded:offer_view');
    event.offerId = info.offerId;
    event.placement = info.placement;
    event.offerType = info.offerType;
    event.surfaceInstanceId = state.surfaceInstanceId;
    event.qualifiedView = true;
    if (info.productId) event.publicProductId = info.productId;
    if (info.variantId) event.publicVariantId = info.variantId;
    enqueue(event);
  }

  function observeSurface(el) {
    if (!surfaceState || !('IntersectionObserver' in window)) return;
    var state = getState(el);
    if (!state || state.viewed || state.observer) return;

    var drawer = el.closest && el.closest('cart-drawer');
    if (drawer && observedDrawers && !observedDrawers.has(drawer)) {
      observedDrawers.add(drawer);
      new MutationObserver(function () {
        // IntersectionObserver measures geometry, not a closed drawer's
        // visibility. Restart observation when its open state changes.
        drawer.querySelectorAll('[data-rd-offer]').forEach(function (surface) {
          var current = surfaceState.get(surface);
          if (!current || current.viewed || !current.observer) return;
          if (current.timer) clearTimeout(current.timer);
          current.timer = null;
          current.observer.unobserve(surface);
          current.observer.observe(surface);
        });
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= VIEW_VISIBLE_RATIO &&
              (!drawer || drawer.classList.contains('active'))) {
            if (!state.timer) {
              state.timer = setTimeout(function () {
                state.timer = null;
                if (state.viewed) return;
                if (drawer && !drawer.classList.contains('active')) return;
                state.viewed = true;
                emitView(el, state);
                observer.disconnect();
              }, VIEW_DWELL_MS);
            }
          } else if (state.timer) {
            clearTimeout(state.timer);
            state.timer = null;
          }
        });
      },
      { threshold: [0, VIEW_VISIBLE_RATIO] }
    );

    observer.observe(el);
    state.observer = observer;
  }

  // ---- Surface discovery -------------------------------------------------

  var SURFACE_SELECTOR = '[data-rd-offer]';

  function scanForSurfaces(root) {
    if (!consentAllowed) return;
    if (!root) return;
    if (root.nodeType !== 1) return;
    if (root.matches && root.matches(SURFACE_SELECTOR)) observeSurface(root);
    if (root.querySelectorAll) {
      var found = root.querySelectorAll(SURFACE_SELECTOR);
      for (var i = 0; i < found.length; i++) observeSurface(found[i]);
    }
  }

  function startObserving() {
    scanForSurfaces(document.body || document.documentElement);
    if (surfaceMutationObserver) return;
    surfaceMutationObserver = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var addedNodes = mutations[m].addedNodes;
        for (var n = 0; n < addedNodes.length; n++) {
          scanForSurfaces(addedNodes[n]);
        }
      }
    });
    surfaceMutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  // ---- Interactions ------------------------------------------------------

  document.addEventListener(
    'click',
    function (event) {
      if (!consentAllowed) return;
      var marker = event.target && event.target.closest && event.target.closest('[data-rd-offer-interaction]');
      if (!marker) return;
      var interactionType = marker.getAttribute('data-rd-offer-interaction');
      if (!VALID_INTERACTIONS[interactionType]) return;
      var surfaceEl = marker.closest(SURFACE_SELECTOR);
      if (!surfaceEl) return;
      var info = surfaceInfo(surfaceEl);
      if (!isValidSurface(info)) return;

      var evt = baseEvent('royded:offer_interaction');
      evt.offerId = info.offerId;
      evt.placement = info.placement;
      evt.offerType = info.offerType;
      evt.interactionType = interactionType;
      var interactionState = getState(surfaceEl);
      if (!interactionState) return;
      evt.surfaceInstanceId = interactionState.surfaceInstanceId;
      if (info.productId) evt.publicProductId = info.productId;
      if (info.variantId) evt.publicVariantId = info.variantId;
      enqueue(evt);
    },
    true
  );

  // ---- Offer added (dispatched by the existing add-to-cart code paths) ---

  document.addEventListener('rd:offer-added', function (event) {
    if (!consentAllowed) return;
    var detail = (event && event.detail) || {};
    if (!detail.offerId || !detail.placement || !detail.offerType) return;

    var evt = baseEvent('royded:offer_added');
    evt.offerId = String(detail.offerId);
    evt.placement = detail.placement;
    evt.offerType = detail.offerType;
    evt.operationId = detail.operationId || uuid();
    var addedSurface = document.querySelector('[data-rd-offer="' + String(detail.offerId).replace(/"/g, '') + '"]');
    var addedState = addedSurface ? getState(addedSurface) : null;
    if (addedState) evt.surfaceInstanceId = addedState.surfaceInstanceId;
    if (detail.variantId != null && detail.variantId !== '') evt.publicVariantId = String(detail.variantId);
    if (detail.productId != null && detail.productId !== '') evt.publicProductId = String(detail.productId);
    if (typeof detail.cartTotalCents === 'number' && isFinite(detail.cartTotalCents)) {
      evt.cartTotalCents = Math.round(detail.cartTotalCents);
    }
    enqueue(evt);
  });

  // ---- Init ---------------------------------------------------------------

  function init() {
    loadConsentApi();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.RDOfferEvents = {
    flush: flush
  };
})();
